import { useRef } from "react";
import { PlaylistItemDTO } from "../../lib/types";
import { cn } from "@/lib/utils";
import * as Sentry from "@sentry/react";

type Props = {
  item: PlaylistItemDTO;
  onAudioEnded: () => void;
  isFullScreen?: boolean;
};

export const DSPlayerAudio: React.FC<Props> = ({ item, onAudioEnded, isFullScreen }) => {
  const ref = useRef<HTMLVideoElement>(null);

  const onTimeUpdate = (currentTime: number) => {
    // If the current time is greater than the duration, pause the audio and call onAudioEnded
    if (currentTime > item.duration && item.duration > 0) {
      ref.current?.pause();
      onAudioEnded();
    }
  };

  return (
    <>
      <img
        className={cn("ds-player-img", isFullScreen && "with-full-screen")}
        src={item.src}
        onError={(event) => {
          Sentry.captureException(new Error(`[DS] Audio cover image load error for ${item.src}`), {
            tags: {
              component: "DSPlayerAudio",
              imageSrc: item.src,
              audioId: item.id.toString(),
            },
            extra: {
              event,
              item,
            },
          });
        }}
      />
      <audio
        ref={ref}
        id={item.id.toString()}
        src={item.audioSrc}
        className="hidden"
        preload="metadata"
        autoPlay={false}
        controls={false}
        loop={false}
        playsInline
        onEnded={onAudioEnded}
        onTimeUpdate={(event) => {
          const audioElement = event.target as HTMLAudioElement;
          if (audioElement?.currentTime !== undefined) {
            onTimeUpdate(audioElement.currentTime);
          }
        }}
        onError={(e) => {
          const audio = e.currentTarget as HTMLAudioElement;
          if (audio.error?.code === 20) {
            console.warn("Autoplay prevented: User interaction required");
          } else {
            console.error("Audio playback error:", e);
            Sentry.captureException(new Error(`[DS] Audio playback error for ${item.audioSrc}`), {
              tags: {
                component: "DSPlayerAudio",
                audioSrc: item.audioSrc,
                audioId: item.id.toString(),
                errorCode: audio.error?.code?.toString() || "unknown",
              },
              extra: {
                event: e,
                item,
                audioError: audio.error,
              },
            });
          }
        }}
      />
    </>
  );
};
